import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy'; // Ícone para erro de agenda
import {
  Box, Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Fade,
  Paper, Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { AgendamentoStep, ResumoEnvioDto } from '../../agendamento.types';
import { AgendamentoService } from '../../services/agendamentoservice';
import { useAgendamentoStore } from '../../store/useAgendamentoStore';
import { InfoAgendamento } from '../agendamento/InfoAgendamento';

export const StepResumo: React.FC = () => {
  const { unidade, servico, data, horario, profissional, setStep } = useAgendamentoStore();
  const [openModalErro, setOpenModalErro] = useState(false); // Estado do modal
  const [loading, setLoading] = useState(false);
  const [contato, setContato] = useState({ nome: '', celular: '' });

  /* =====================================================
     VALIDAÇÃO DINÂMICA
  ===================================================== */
  const isValido = useMemo(() => {
    const nomeLimpo = contato.nome.trim();
    const temNomeCompleto = nomeLimpo.split(/\s+/).length >= 2 && nomeLimpo.length >= 6;

    const apenasNumeros = contato.celular.replace(/\D/g, '');
    const celularValido = apenasNumeros.length >= 10 && apenasNumeros.length <= 11;

    return temNomeCompleto && celularValido;
  }, [contato]);

  const temValorDiferenciado = (profissional?.valor || 0) > 0;
  const valorTotal = temValorDiferenciado ? (profissional?.valor || 0) : (servico?.valor || 0);
  const handleFecharErro = () => {
    setOpenModalErro(false);
    setStep(AgendamentoStep.HORARIOS); // Redireciona para horários
  };

  const handleFinalizar = async () => {
    if (!isValido) return;

    setLoading(true);

    const payload: ResumoEnvioDto = {
      nome: contato.nome.trim(),
      celular: contato.celular.replace(/\D/g, ''),
      data: data!,
      horario: horario!,
      executante: { codigo: profissional!.codigo },
      unidade: { codigo: unidade!.codigo },
      produto: { codigo: servico!.codigo }
    };



    await AgendamentoService.agendar(payload).then(() => {
      setStep(AgendamentoStep.CONFIRMACAO);
    }).catch((error: any) => {


      if (error?.response?.data?.codeResponse === 'ERRO_AGENDA') {
        setOpenModalErro(true);
      } else {
        alert("Ocorreu um erro ao processar seu agendamento.");
      }

    }).finally(() => {
      setLoading(false);
    })
  };

  return (
    <Fade in timeout={600}>
      <Box>
        <InfoAgendamento />

        <Typography variant="h6" fontWeight={800} mb={3} sx={{ color: 'var(--color-text)' }}>
          Finalizar Agendamento
        </Typography>

        <Stack spacing={3}>
          {/* Dados de Contato */}
          <Paper sx={{ p: 3, borderRadius: 6, border: '1px solid rgba(128,128,128,0.2)', bgcolor: 'var(--color-surface)' }}>
            <Typography variant="subtitle2" fontWeight={800} mb={2} sx={{ color: 'var(--color-text)' }}>Seus Dados</Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Nome completo"
                variant="outlined"
                value={contato.nome}
                onChange={(e) => setContato({ ...contato, nome: e.target.value })}
                error={contato.nome.length > 5 && contato.nome.trim().split(/\s+/).length < 2}
                helperText={contato.nome.length > 5 && contato.nome.trim().split(/\s+/).length < 2 ? "Informe nome e sobrenome" : ""}
                sx={{ '& .MuiInputBase-input': { color: 'var(--color-text)' } }}
              />

              <PatternFormat
                format="(##) #####-####"
                mask="_"
                customInput={TextField}
                fullWidth
                label="WhatsApp / Celular"
                variant="outlined"
                value={contato.celular}
                onValueChange={(values) => {
                  setContato({ ...contato, celular: values.value });
                }}
                error={contato.celular.length > 0 && contato.celular.length < 10}
                helperText={contato.celular.length > 0 && contato.celular.length < 10 ? "Número incompleto" : ""}
                sx={{ '& .MuiInputBase-input': { color: 'var(--color-text)' } }}
              />
            </Stack>
          </Paper>

          {/* Card de Valores */}
          <Paper sx={{ p: 3, borderRadius: 6, border: '1px solid rgba(128,128,128,0.2)', bgcolor: 'var(--color-surface)' }}>
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: 'var(--color-text)', opacity: 0.7 }}>
                  {servico?.descricao} {temValorDiferenciado && `(${profissional?.nome})`}
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: 'var(--color-text)' }}>
                  {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Box>

              {temValorDiferenciado && (
                <Typography variant="caption" sx={{ color: 'var(--color-text)', opacity: 0.5, fontStyle: 'italic' }}>
                  * Valor exclusivo para atendimento com {profissional?.nome}
                </Typography>
              )}

              <Divider sx={{ my: 1, borderColor: 'rgba(128,128,128,0.1)' }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={800} sx={{ color: 'var(--color-text)' }}>Total a pagar</Typography>
                <Typography variant="h6" fontWeight={900} color="var(--color-primary)">
                  {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleFinalizar}
            disabled={!isValido || loading}
            startIcon={!loading && <CheckCircleOutlineIcon />}
            sx={{
              py: 2,
              borderRadius: 4,
              fontWeight: 800,
              bgcolor: 'var(--color-primary)',
              color: 'var(--color-background)',
              transition: '0.3s',
              '&:disabled': {
                bgcolor: 'rgba(128,128,128,0.2)',
                color: 'rgba(128,128,128,0.5)'
              },
              '&:hover': {
                bgcolor: 'var(--color-primary)',
                filter: 'brightness(1.1)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'CONFIRMAR E AGENDAR'}
          </Button>
        </Stack>
        {/* MODAL DE ERRO DE AGENDA */}
        <Dialog
          open={openModalErro}
          onClose={handleFecharErro}
          PaperProps={{
            sx: { borderRadius: 5, p: 2, textAlign: 'center', maxWidth: 400 }
          }}
        >
          <DialogContent>
            <EventBusyIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Horário Indisponível
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              Que pena! Enquanto você preenchia os dados, outra pessoa finalizou o agendamento para este horário.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: 'var(--color-primary)' }}>
              Não se preocupe, vamos te mostrar os horários disponíveis novamente!
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button
              onClick={handleFecharErro}
              variant="contained"
              fullWidth
              sx={{ borderRadius: 3, fontWeight: 700, bgcolor: 'var(--color-primary)' }}
            >
              ESCOLHER OUTRO HORÁRIO
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};